import { useState, useEffect, useCallback } from "react";
import { useApi } from "../hooks/useApi";
import Sidebar from "../components/Sidebar";
import { Topbar, PageError, friendlyError } from "../components";
import type { Connector } from "./Dashboard";

interface ConnectorType {
  id:           string;
  name:         string;
  description:  string;
  logo:         string;
  available:    boolean;
  comingSoon?:  boolean;
}

const CONNECTOR_TYPES: ConnectorType[] = [
  {
    id:          "freeagent",
    name:        "FreeAgent",
    description: "Invoices, bills, contacts, expenses, payroll, VAT returns",
    logo:        "FA",
    available:   true,
  },
  {
    id:          "xero",
    name:        "Xero",
    description: "Invoices, bills, contacts, accounts, journals, reports & more",
    logo:        "X",
    available:   true,
  },
];

export default function ConnectorsPage() {
  const { get, post, del } = useApi();

  const [connectors, setConnectors]     = useState<Connector[]>([]);
  const [loading, setLoading]           = useState(true);
  const [adding, setAdding]             = useState<string | null>(null);
  const [connecting, setConnecting]     = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState<ConnectorType | null>(null);
  const [apiType, setApiType]           = useState<"company" | "practice">("company");
  const [displayName, setDisplayName]   = useState("");
  const [error, setError]               = useState("");
  const [successMsg, setSuccessMsg]     = useState("");
  const [pageError, setPageError]       = useState<string | null>(null);

  const loadConnectors = useCallback(() => {
    return get<{ connectors: Connector[] }>("/api/connectors")
      .then((res) => setConnectors(res.connectors))
      .catch(err => setPageError(friendlyError(err)))
      .finally(() => setLoading(false));
  }, [get]);

  useEffect(() => { loadConnectors(); }, [loadConnectors]);

  // Handle OAuth callback — ?connected=freeagent or ?error=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const oauthError = params.get("error");

    if (connected) {
      setSuccessMsg(`${connected} connected successfully`);
      loadConnectors();
      window.history.replaceState({}, "", "/connectors");
    } else if (oauthError) {
      setError(`OAuth failed: ${oauthError}`);
      window.history.replaceState({}, "", "/connectors");
    }
  }, [loadConnectors]);

  const handleAdd = async () => {
    if (!showAddModal || !displayName.trim()) return;
    setAdding(showAddModal.id);
    setError("");
    try {
      const res = await post<{ connector: Connector }>("/api/connectors", {
        connector_type: showAddModal.id,
        display_name:   displayName.trim(),
      });
      await startOAuth(res.connector.id, showAddModal.id, apiType);
    } catch (e: any) {
      setError(e.message ?? "Failed to create connector");
      setAdding(null);
    }
  };

  const startOAuth = async (connectorId: string, connectorType: string, type: "company" | "practice") => {
    setConnecting(connectorId);
    try {
      const res = await get<{ auth_url: string }>(
        `/api/connectors/${connectorId}/${connectorType}-auth?type=${type}`
      );
      window.location.href = res.auth_url;
    } catch (e: any) {
      setError(e.message ?? "Failed to start OAuth");
      setConnecting(null);
    }
  };

  const handleDisconnect = async (connectorId: string) => {
    if (!confirm("Disconnect this connector? Any scheduled backups using it will be paused.")) return;
    try {
      await del(`/api/connectors/${connectorId}`);
      setConnectors((prev) => prev.filter((c) => c.id !== connectorId));
    } catch (e: any) {
      setError(e.message ?? "Failed to disconnect");
    }
  };

  const connectedIds = new Set(connectors.map((c) => c.connector_type));

  return (
    <div className="shell">
      <Sidebar />
      <div className="main-column">
        <Topbar
          title="Connectors"
          subtitle={`${connectors.length} connected · ${CONNECTOR_TYPES.filter(c => c.available).length} available`}
        />
        <main className="page">

          {pageError && <PageError message={pageError} onRetry={loadConnectors} />}

          {!pageError && (<>
          {successMsg && (
            <div className="alert alert-success" style={{ marginBottom: 16 }}>
              ✓ {successMsg}
              <button onClick={() => setSuccessMsg("")} style={{ marginLeft: 12, background: "none", border: "none", cursor: "pointer", color: "inherit" }}>✕</button>
            </div>
          )}
          {error && (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              {error}
              <button onClick={() => setError("")} style={{ marginLeft: 12, background: "none", border: "none", cursor: "pointer", color: "inherit" }}>✕</button>
            </div>
          )}

          {/* Connected connectors */}
          {connectors.length > 0 && (
            <section style={{ marginBottom: 28 }}>
              <div className="section-label">Connected</div>
              <div className="connector-cards">
                {connectors.map((c) => (
                  <ConnectorCard
                    key={c.id}
                    connector={c}
                    onReconnect={() => startOAuth(c.id, c.connector_type, "company")}
                    onDisconnect={() => handleDisconnect(c.id)}
                    connecting={connecting === c.id}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Available connectors */}
          <section>
            <div className="section-label">Available</div>
            <div className="connector-cards">
              {CONNECTOR_TYPES.map((type) => (
                <AvailableCard
                  key={type.id}
                  connectorType={type}
                  isConnected={connectedIds.has(type.id)}
                  onAdd={() => { setShowAddModal(type); setDisplayName(type.name); setError(""); }}
                />
              ))}
            </div>
          </section>

          </>)}
        </main>
      </div>

      {/* ── Add connector modal ── */}
      {showAddModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowAddModal(null)}>
          <div className="modal">
            <div className="modal-head">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="connector-logo-lg">{showAddModal.logo}</div>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 15 }}>Connect {showAddModal.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>{showAddModal.description}</div>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowAddModal(null)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Display name</label>
                <input
                  type="text"
                  className="form-input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Acme Accounting Ltd"
                  autoFocus
                />
              </div>

              {showAddModal.id === "freeagent" && (
                <div className="form-group">
                  <label className="form-label">API type</label>
                  <div className="radio-group">
                    <label className={`radio-option ${apiType === "company" ? "selected" : ""}`}>
                      <input type="radio" name="apiType" value="company"
                        checked={apiType === "company"}
                        onChange={() => setApiType("company")} />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>Company API</div>
                        <div style={{ fontSize: 12, color: "var(--text3)" }}>A single FreeAgent business account</div>
                      </div>
                    </label>
                    <label className={`radio-option ${apiType === "practice" ? "selected" : ""}`}>
                      <input type="radio" name="apiType" value="practice"
                        checked={apiType === "practice"}
                        onChange={() => setApiType("practice")} />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>Practice API</div>
                        <div style={{ fontSize: 12, color: "var(--text3)" }}>Accountancy practice managing multiple clients</div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {error && <div className="alert alert-error" style={{ marginBottom: 0 }}>{error}</div>}
            </div>

            <div className="modal-foot">
              <button className="btn btn-ghost" onClick={() => setShowAddModal(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleAdd}
                disabled={!displayName.trim() || !!adding}
              >
                {adding ? "Connecting…" : `Connect ${showAddModal.name} →`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── ConnectorCard — shows a live connected connector ─────────────────────────
function ConnectorCard({ connector, onReconnect, onDisconnect, connecting }: {
  connector:   Connector;
  onReconnect: () => void;
  onDisconnect: () => void;
  connecting:  boolean;
}) {
  const isExpired = connector.token_expires_at
    ? new Date(connector.token_expires_at) < new Date()
    : false;

  return (
    <div className={`connector-card ${!connector.is_active || isExpired ? "connector-card--warning" : ""}`}>
      <div className="connector-card-top">
        <div className="connector-logo-lg">{connector.connector_type[0].toUpperCase()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="connector-card-name">{connector.display_name}</div>
          <div className="connector-card-type">{connector.connector_type}</div>
        </div>
        {connector.is_active && !isExpired
          ? <span className="badge badge-success">connected</span>
          : <span className="badge badge-warn">needs auth</span>
        }
      </div>
      <div className="connector-card-actions">
        {(!connector.is_active || isExpired) && (
          <button className="btn btn-primary btn-sm" onClick={onReconnect} disabled={connecting}>
            {connecting ? "Redirecting…" : "Reconnect"}
          </button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={onDisconnect}>
          Disconnect
        </button>
      </div>
    </div>
  );
}

// ── AvailableCard — shows an available connector type to add ──────────────────
function AvailableCard({ connectorType, isConnected, onAdd }: {
  connectorType: ConnectorType;
  isConnected:   boolean;
  onAdd:         () => void;
}) {
  return (
    <div className={`connector-card ${connectorType.comingSoon ? "connector-card--muted" : ""}`}>
      <div className="connector-card-top">
        <div className="connector-logo-lg">{connectorType.logo}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="connector-card-name">{connectorType.name}</div>
          <div className="connector-card-type">{connectorType.description}</div>
        </div>
        {connectorType.comingSoon && (
          <span className="badge badge-neutral">soon</span>
        )}
      </div>
      {!connectorType.comingSoon && (
        <div className="connector-card-actions">
          <button
            className="btn btn-primary btn-sm"
            onClick={onAdd}
            disabled={isConnected}
          >
            {isConnected ? "Already connected" : "+ Add connector"}
          </button>
        </div>
      )}
    </div>
  );
}
